import { UpdateResolver } from '@urql/exchange-graphcache';
import {
  BulkCreateShapesMutation,
  BulkCreateShapesMutationVariables,
  BulkDeleteShapesMutation,
  BulkDeleteShapesMutationVariables,
  BulkUpdateShapesMutation,
  BulkUpdateShapesMutationVariables,
  CreateShapeMutation,
  CreateShapeMutationVariables,
  DeleteShapeMutation,
  DeleteShapeMutationVariables,
} from '../../gql/graphql';

export const createShapeResolver: UpdateResolver = (
  result: CreateShapeMutation,
  args: CreateShapeMutationVariables,
  cache
) => {
  if (result.createShape?.shape) {
    const shapesForProject = cache.resolve(
      { __typename: 'Project', id: result.createShape.shape.projectId },
      'shapes'
    ) as string[] | null;
    const shapeToAdd = cache.keyOfEntity({
      __typename: 'Shape',
      id: result.createShape.shape.id,
    });
    if (shapesForProject && shapeToAdd) {
      shapesForProject.push(shapeToAdd);
      cache.link(
        {
          __typename: 'Project',
          id: result.createShape.shape.projectId,
        },
        'shapes',
        shapesForProject
      );
    }
  }
};

export const deleteShapeResolver: UpdateResolver = (
  result: DeleteShapeMutation,
  args: DeleteShapeMutationVariables,
  cache
) => {
  if (result.deleteShape?.shape) {
    const shapesForProject = cache.resolve(
      { __typename: 'Project', id: result.deleteShape.shape.projectId },
      'shapes'
    ) as string[] | null;
    const shapeToRemove = cache.keyOfEntity({
      __typename: 'Shape',
      id: result.deleteShape.shape.id,
    });
    if (shapesForProject && shapeToRemove) {
      cache.link(
        {
          __typename: 'Project',
          id: result.deleteShape.shape.projectId,
        },
        'shapes',
        shapesForProject.filter((key) => key !== shapeToRemove)
      );
    }
  }
};

export const bulkDeleteShapesResolver: UpdateResolver = (
  result: BulkDeleteShapesMutation,
  args: BulkDeleteShapesMutationVariables,
  cache
) => {
  const deletedShapes = result.bulkDeleteShapes?.shapes;
  if (!deletedShapes || deletedShapes.length === 0) return;

  // Group deleted shapes by projectId for efficient cache updates
  const shapesByProject = deletedShapes.reduce((acc, shape) => {
    if (!acc[shape.projectId]) {
      acc[shape.projectId] = [];
    }
    acc[shape.projectId].push(shape.id);
    return acc;
  }, {} as Record<string, string[]>);

  // Update cache for each project
  for (const [projectId, deletedIds] of Object.entries(shapesByProject)) {
    const shapesForProject = cache.resolve(
      { __typename: 'Project', id: projectId },
      'shapes'
    ) as string[] | null;

    if (shapesForProject) {
      const keysToRemove = new Set(
        deletedIds.map((id) => cache.keyOfEntity({ __typename: 'Shape', id }))
      );
      cache.link(
        { __typename: 'Project', id: projectId },
        'shapes',
        shapesForProject.filter((key) => !keysToRemove.has(key))
      );
    }
  }
};

export const bulkCreateShapesResolver: UpdateResolver = (
  result: BulkCreateShapesMutation,
  args: BulkCreateShapesMutationVariables,
  cache
) => {
  const createdShapes = result.bulkCreateShapes?.shapes;
  if (!createdShapes || createdShapes.length === 0) return;

  // Group created shapes by projectId for efficient cache updates
  const shapesByProject = createdShapes.reduce((acc, shape) => {
    if (!acc[shape.projectId]) {
      acc[shape.projectId] = [];
    }
    acc[shape.projectId].push(shape.id);
    return acc;
  }, {} as Record<string, string[]>);

  // Update cache for each project
  for (const [projectId, createdIds] of Object.entries(shapesByProject)) {
    const shapesForProject = cache.resolve(
      { __typename: 'Project', id: projectId },
      'shapes'
    ) as string[] | null;

    if (shapesForProject) {
      const keysToAdd = createdIds
        .map((id) => cache.keyOfEntity({ __typename: 'Shape', id }))
        .filter((key): key is string => key !== null);
      cache.link({ __typename: 'Project', id: projectId }, 'shapes', [
        ...shapesForProject,
        ...keysToAdd,
      ]);
    }
  }
};

// Bulk update resolver - the normalized cache automatically updates shape data
// since we return the full ShapeDeep fragment. This resolver is here for consistency.
export const bulkUpdateShapesResolver: UpdateResolver = (
  _result: BulkUpdateShapesMutation,
  _args: BulkUpdateShapesMutationVariables,
  _cache
) => {
  // No-op: URQL's normalized cache automatically updates entities
  // when we return data with matching __typename and id
};
